'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import HouseAd from './HouseAd';
import { useAdFallback } from './useAdFallback';

const ADSENSE_CLIENT_ID = 'ca-pub-2214184889025947';

/**
 * Manual AdSense display ad unit (responsive), with a house-ad fallback.
 *
 * Used for placements where Auto Ads underperforms — notably interactive tool
 * pages, where there's little article body for Auto Ads to inject into. A fixed
 * <ins> slot guarantees an impression on every pageview in a known position.
 *
 * The adsbygoogle.js loader is provided by <AdSenseLoader /> rendered on the same
 * (consumer) page. Reserves vertical space (min-height) to limit layout shift
 * (CLS). The push() is guarded so React StrictMode's double-invoke in dev does
 * not double-fill the same slot.
 *
 * HOUSE-AD FALLBACK: ~70% of ad requests here go unfilled, leaving an empty slot
 * that earns nothing. When useAdFallback determines AdSense declined this slot,
 * we render one of our own SaaS products instead. AdSense always gets first
 * refusal — the check runs on a delay and resolves every ambiguous state to
 * "filled", so a paying impression is never displaced. See lib/house-ads.ts.
 */
export default function AdUnit({ slot }: { slot: string }) {
  const pushed = useRef(false);
  const insRef = useRef<HTMLModElement | null>(null);
  const pathname = usePathname();
  const { unfilled, blocked } = useAdFallback(insRef, { slot: slot, placement: 'in-content' });

  useEffect(() => {
    if (pushed.current) return;
    pushed.current = true;
    try {
      const w = window as unknown as { adsbygoogle: unknown[] };
      (w.adsbygoogle = w.adsbygoogle || []).push({});
    } catch {
      // Loader not ready yet or slot already filled — safe to ignore.
    }
  }, []);

  return (
    <section className="py-8 px-4">
      <div className="max-w-7xl mx-auto text-center">
        {/* Label only while this is genuinely a third-party ad. HouseAd carries
            its own "From the Inventive HQ family" label — calling our own
            product an "Advertisement" would be misleading, and AdSense requires
            non-Google ads not be presented as though they were Google's. */}
        {!unfilled && (
          <div className="mb-1 select-none text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Advertisement
          </div>
        )}
        {/* Unmount the <ins> once it is known unfilled rather than just hiding
            it. Ad blockers apply cosmetic filters as well as network blocks,
            and a :has(> ins.adsbygoogle) rule would hide this whole container —
            taking the house ad with it. Removing the element means such a rule
            has nothing to match. Detection has already completed by this point
            (useAdFallback settles once), so the ref is no longer needed. */}
        {!unfilled && (
          <ins
            ref={insRef}
            className="adsbygoogle"
            style={{
              display: 'block',
              minHeight: 100,
            }}
            data-ad-client={ADSENSE_CLIENT_ID}
            data-ad-slot={slot}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        )}
        {unfilled && (
          <HouseAd pathname={pathname} blocked={blocked} className="mx-auto max-w-2xl text-left" />
        )}
      </div>
    </section>
  );
}
